/**
 * Package Guest Products ✦ — CANONICAL SOURCE OF TRUTH
 * ────────────────────────────────────────────────────────
 * iter188 · 2026-05-25 · Craig lock (Booth OS Consolidation)
 *
 * The Room ✦ and The Archive ✦ now flow through Booth OS as the
 * primary guest participation layer. Keepsake remains lightweight
 * and continues to use the dashboard grid.
 *
 *   QR → RSVP / welcome → "Enter Tonight ✦" → Booth OS  (Room, Archive)
 *   QR → RSVP / welcome → dashboard grid              (Keepsake)
 *
 * Two product views are exposed:
 *
 *   getGuestProducts(event)    — legacy dashboard grid (Keepsake + the
 *                                personal-link GuestDashboard for any
 *                                package that doesn't go via Booth OS)
 *   getBoothOSProducts(event)  — the 11-card vertical OS for Booth OS.
 *                                Returns {active, dimmed} state per
 *                                card so Room can render the three
 *                                Archive-only products in a calm
 *                                "Available inside The Archive ✦"
 *                                state — visible but non-clickable,
 *                                no padlock, no upsell vocabulary.
 *
 * Mapping note (key stability):
 *   We retain the existing backend canonical feature ids
 *   (qr_upload, memory_wall_card, video_guestbook, memory_guestbook,
 *   full_event_film). The iter188 spec lists display-aliases
 *   (upload_memories, memory_wall, audio_video_guestbook,
 *   flip_guestbook, film). The mapping is local to this file so the
 *   backend FEATURES_CATALOG stays untouched.
 */

import {
  Camera, BookOpen, Microphone, ImageSquare, Quotes, Lightning,
  Play, FilmStrip, PencilSimple, Crown, AppWindow, Newspaper,
} from '@phosphor-icons/react';

// ──────────────────────────────────────────────────────────────────
// Product registry — every guest-facing product. Keyed by canonical
// backend feature id. DO NOT rename keys without updating the
// backend FEATURES_CATALOG in lockstep.
// ──────────────────────────────────────────────────────────────────
export const GUEST_PRODUCT_REGISTRY = {
  qr_upload: {
    id: 'qr_upload',
    title: 'Upload Memories',
    subtitle: 'Add a moment from tonight — photo or video.',
    icon: Camera,
    accent: 'gold',
    // Inline file picker — no navigation hop. Surface-specific glue
    // (GuestDashboard, GuestAccessPage, BoothLauncher) wires this.
    action: 'open-uploader',
  },
  memory_snap: {
    id: 'memory_snap',
    title: 'Memory Snap',
    subtitle: 'A polaroid moment from each guest.',
    icon: ImageSquare,
    accent: 'gold',
    route: (slug) => `/event/${slug}/memory-snap`,
  },
  memory_wall_card: {
    id: 'memory_wall_card',
    title: 'Memory Wall',
    subtitle: 'A living wall of every photo and message.',
    icon: ImageSquare,
    accent: 'gold',
    route: (slug) => `/event/${slug}/memory-wall`,
  },
  video_guestbook: {
    id: 'video_guestbook',
    title: 'Audio / Video Guestbook',
    subtitle: 'Record a short message to the night.',
    icon: Microphone,
    accent: 'teal',
    route: (slug) => `/event/${slug}/guestbook`,
  },
  memory_guestbook: {
    id: 'memory_guestbook',
    title: 'Flip Guestbook',
    subtitle: 'Add a written page to the keepsake book.',
    icon: BookOpen,
    accent: 'cyan',
    route: (slug) => `/event/${slug}/memory-book-message`,
  },
  lens_notes: {
    id: 'lens_notes',
    title: 'Lens Notes ✦',
    subtitle: 'Handwriting over video. A message that stays.',
    icon: PencilSimple,
    accent: 'gold',
    route: (slug) => `/lens-note/${slug}`,
  },
  drop_one_word: {
    id: 'drop_one_word',
    title: 'Drop One Word ✦',
    subtitle: 'One word. One feeling. The atmosphere of the night.',
    icon: Quotes,
    accent: 'gold',
    route: (slug) => `/one-word/${slug}`,
  },
  pocket_booth: {
    id: 'pocket_booth',
    title: 'Pocket Booth',
    subtitle: 'Capture the room — a phone-based booth in your hand.',
    icon: Camera,
    accent: 'pink',
    route: (slug) => `/event/${slug}/pocket-booth`,
  },
  spin_film_360: {
    id: 'spin_film_360',
    title: 'Spin Film 360',
    subtitle: 'A slow-motion 360 spin for every guest.',
    icon: Camera,
    accent: 'gold',
    route: (slug) => `/spin?slug=${encodeURIComponent(slug)}`,
  },
  full_event_film: {
    id: 'full_event_film',
    title: 'Film',
    subtitle: 'The composed cinematic record of the night.',
    icon: FilmStrip,
    accent: 'violet',
    route: (slug) => `/film/${slug}`,
  },
  highlight_reel: {
    id: 'highlight_reel',
    title: 'Highlight Reel',
    subtitle: 'A short, composed cut of the night.',
    icon: Play,
    accent: 'gold',
    route: (slug) => `/event/${slug}/memory-wall?notice=highlight-composing`,
  },
  // iter196 — Remember Us ✦ universal participation surface.
  // The shared wall is the participation substrate (Belief 2); it
  // belongs in every guest-facing matrix that exposes participation,
  // not just the dashboard companions grid.
  remember_us: {
    id: 'remember_us',
    title: 'Remember Us ✦',
    subtitle: 'Add a mark, a message, a signature — the wall the year group leaves behind.',
    icon: PencilSimple,
    accent: 'gold',
    route: (slug) => `/remember-us/${slug}`,
  },
  // iter200b — The Final Night ✦ archival companion.
  //
  // The Chronicle is NOT a post-event export utility, dashboard
  // attachment, admin report, or downloadable afterthought. It is
  // the final layer of the room remembering itself — present
  // *during* the evening as a quiet archival layer that gathers
  // photos, voices, handwritten marks, dancefloor moments and
  // fragments of the year group into one archival edition after
  // the evening settles.
  //
  // Locked editorial copy from the iter200b brief. The card
  // subtitle is the "body copy" line; the "supporting line" lives
  // on the FinalNightPage itself. No CTA-state vocabulary lands
  // here — page state-awareness handles that downstream.
  final_night: {
    id: 'final_night',
    title: 'The Final Night ✦',
    subtitle: 'A printed remembrance of the room, composed from the moments everyone left behind.',
    icon: Newspaper,
    accent: 'gold',
    route: (slug) => `/event/${slug}/final-night`,
  },
  // iter206 — The Documentary ✦. The long-form emotional narrative
  // artefact: 3–6 minutes assembled from photos, voices, group
  // moments and Lens Notes across three movements (Before We Leave
  // / The Final Night / What Remains). NOT a recap video, NOT a
  // highlight reel, NOT a social-content cut — those exist as
  // distinct artefacts elsewhere in the system. The Documentary
  // is the emotional synthesis layer, the room reflecting on
  // itself after the music fades.
  documentary: {
    id: 'documentary',
    title: 'The Documentary ✦',
    subtitle: 'A long-form remembrance of the room — its voices, its faces, its quiet aftermath.',
    icon: FilmStrip,
    accent: 'gold',
    route: (slug) => `/event/${slug}/documentary`,
  },
  // iter208 — Silent Film ✦. The poetic fragment artefact: a 20–45s
  // black & white vertical recap composed from the same ChroniclePlan
  // the Final Night and Documentary share. Distinct from the long-
  // form Documentary — Silent Film is the social-first poetic
  // fragment; Final Night is the printed chronicle; Documentary is
  // the emotional synthesis. Three siblings, three preservation
  // modes, registered explicitly here so the taxonomy layering can
  // group all three together under the archive section.
  silent_film: {
    id: 'silent_film',
    title: 'Silent Film ✦',
    subtitle: 'A short black & white fragment of the evening, remembered like cinema.',
    icon: FilmStrip,
    accent: 'gold',
    route: (slug) => `/event/${slug}/silent-film`,
  },
  // ── Legacy products kept for dashboard fallback ──
  challenge_mode: {
    id: 'challenge_mode',
    title: 'Mega Missions',
    subtitle: 'Timed quests that stir the room into motion.',
    icon: Lightning,
    accent: 'violet',
    route: (slug) => `/event/${slug}/challenges`,
  },
  booth_os: {
    id: 'booth_os',
    title: 'Booth OS',
    subtitle: 'The kiosk at the room.',
    icon: AppWindow,
    accent: 'gold',
    route: (slug, event) => `/booth-os/${event?.event_id || slug}`,
  },
  pro: {
    id: 'pro',
    title: 'Pro Upgrade',
    subtitle: 'Premium polish — watermark-free, full quality.',
    icon: Crown,
    accent: 'gold',
    route: () => `/pricing`,
  },
};

// ──────────────────────────────────────────────────────────────────
// LEGACY DASHBOARD MATRIX — used by Keepsake + the personal-link
// GuestDashboard fallback. Room and Archive bypass this entirely and
// go through Booth OS instead.
// ──────────────────────────────────────────────────────────────────
export const PACKAGE_GUEST_PRODUCTS = {
  edition_keepsake: [
    'qr_upload',         // Upload Memories
    'memory_snap',       // Memory Snap
    'memory_wall_card',  // Memory Wall
    'drop_one_word',     // Drop One Word
    'memory_guestbook',  // Flip Guestbook
  ],
  // Room and Archive: the dashboard grid is no longer the entry point —
  // they enter through Booth OS. Lists retained here for legacy callers
  // (e.g. the personal-link GuestDashboard) so nothing breaks if a
  // host still shares the personal link instead of the QR code.
  edition_room: [
    'qr_upload', 'memory_snap', 'memory_wall_card', 'video_guestbook',
    'memory_guestbook', 'lens_notes', 'drop_one_word', 'pocket_booth',
  ],
  edition_archive: [
    'qr_upload', 'memory_snap', 'memory_wall_card', 'video_guestbook',
    'memory_guestbook', 'lens_notes', 'drop_one_word', 'pocket_booth',
    'spin_film_360', 'full_event_film', 'highlight_reel',
    // iter208 — Archive package surfaces all three preservation
    // artefacts. The taxonomy layering keeps them grouped under
    // the archive section in the canonical order: Final Night →
    // Silent Film → Documentary → Highlight Reel → Event Film.
    'final_night', 'silent_film', 'documentary',
  ],
  // iter196 — Leavers Night guest surface (Belief 5: schools
  // emotional centre = collective identity / momentum / belonging).
  // The £99 Leavers Night ✦ package: scan-and-enter, no formal
  // invitation list, no name gating. Remember Us ✦ sits high in the
  // list because it IS the participation centrepiece for this edition
  // (mirrors the dashboard's elevated "Leave your mark" section).
  // Archive-tier cinematic products (spin_film_360, full_event_film,
  // highlight_reel, lens_notes, memory_guestbook) are deliberately
  // absent — they belong to weddings, not Leavers (iter193 lock).
  // iter200b — Leavers Night also bundles The Final Night ✦.
  // Positioned AFTER remember_us so the emotional hierarchy stays:
  //   1. participation (Remember Us)
  //   2. atmosphere (Memory Wall, Drop One Word)
  //   3. preservation (The Final Night)
  edition_leavers_night: [
    'qr_upload',         // Upload Memories
    'remember_us',       // Remember Us ✦ — the wall the year group writes together
    'final_night',       // The Final Night ✦ — archival edition forming during the evening
    'silent_film',       // Silent Film ✦ — poetic fragment (iter208)
    'documentary',       // The Documentary ✦ — long-form emotional narrative (iter206)
    'memory_snap',       // Memory Snap
    'memory_wall_card',  // Memory Wall (live photo feed)
    'pocket_booth',      // Pocket Booth (included in the edition)
    'video_guestbook',   // Audio / Video Guestbook (included)
    'drop_one_word',     // Drop One Word ✦
    'challenge_mode',    // Mega Missions (included via challenges_purchased)
  ],
};

// ──────────────────────────────────────────────────────────────────
// BOOTH OS MATRIX — the 11-card vertically-scrolling experience OS.
// Order is the iter188 canonical lock and MUST NOT drift.
// ──────────────────────────────────────────────────────────────────
const BOOTH_OS_CANONICAL_ORDER = [
  'qr_upload',         // Upload Memories
  'memory_snap',       // Memory Snap
  'memory_wall_card',  // Memory Wall
  'video_guestbook',   // Audio / Video Guestbook
  'memory_guestbook',  // Flip Guestbook
  'lens_notes',        // Lens Notes
  'drop_one_word',     // Drop One Word
  'pocket_booth',      // Pocket Booth / Capture the Room
  'spin_film_360',     // Archive only (dimmed on Room)
  'full_event_film',   // Archive only (dimmed on Room)
  'highlight_reel',    // Archive only (dimmed on Room)
  'final_night',       // iter200b — Archive only (dimmed on Room)
  'documentary',       // iter206 — long-form narrative artefact
];

// ── iter189 emotional flow ──────────────────────────────────────
// Four editorial sections that pace the evening. Cards stay in the
// canonical order *within* a section. Sections in the order below.
export const BOOTH_OS_SECTIONS = [
  {
    id: 'capture',
    title: 'Capture the room ✦',
    subtitle: 'Add moments from tonight as they happen.',
    tone: 'energetic',
    products: ['qr_upload', 'memory_snap', 'pocket_booth', 'spin_film_360'],
  },
  {
    id: 'leave_behind',
    title: 'Leave something behind ✦',
    subtitle: 'Words, voices, handwriting, feelings.',
    tone: 'centre',
    products: ['video_guestbook', 'memory_guestbook', 'lens_notes', 'drop_one_word'],
  },
  {
    id: 'see_forming',
    title: 'See the night forming ✦',
    subtitle: 'Watch the atmosphere build around you.',
    tone: 'living',
    products: ['memory_wall_card'],
  },
  {
    id: 'preserve',
    title: 'Preserve the night ✦',
    subtitle: 'What survives after the music fades.',
    tone: 'archival',
    // iter200b — The Final Night ✦ leads the preserve section. The
    // Chronicle is the final layer of the room remembering itself;
    // it sits ABOVE the cinematic film cuts because it is the
    // archival edition all those moments compose into.
    //
    // iter206 — The Documentary ✦ sits AFTER The Final Night as
    // the emotional synthesis layer (long-form narrative).
    //
    // iter208 — Silent Film ✦ now sits BETWEEN Final Night and
    // Documentary so the three preservation siblings render in the
    // canonical Final Night → Silent Film → Documentary order
    // everywhere in the system. The cinematic cuts (highlight reel /
    // full film) follow because they are social-first artefacts,
    // not archival ones.
    //
    // Booth OS is an OPERATOR surface — it keeps the stewardship
    // register ("Preserve the night ✦") rather than the aftermath
    // register ("What remains ✦") used on guest-facing surfaces.
    // The distinction is constitutional, not stylistic.
    products: ['final_night', 'silent_film', 'documentary', 'full_event_film', 'highlight_reel'],
  },
];

// Editorial helper copy under each card title. Short. Quiet.
// One emotional line; never marketing.
const BOOTH_CARD_HELPER_COPY = {
  qr_upload:        'A moment, kept ✦',
  memory_snap:      'A polaroid pulled from tonight ✦',
  pocket_booth:     'The room, captured quietly ✦',
  spin_film_360:    'A slow 360 of you in motion ✦',
  video_guestbook:  'A voice for tomorrow ✦',
  memory_guestbook: 'A page in the keepsake book ✦',
  lens_notes:       'Handwriting that stays ✦',
  drop_one_word:    'A feeling left behind ✦',
  memory_wall_card: 'The room as it is, right now ✦',
  full_event_film:  'The film the night becomes ✦',
  highlight_reel:   'The shortest, sharpest cut ✦',
  // iter200b — The Chronicle gathers quietly while the evening
  // unfolds. The helper line is observational, never an action.
  final_night:      'The archival edition, quietly forming ✦',
  // iter208 — Silent Film ✦. The poetic fragment artefact, sibling
  // to Final Night and Documentary in the preservation layer.
  silent_film:      'A short fragment, remembered like cinema ✦',
  // iter206 — The Documentary gathers voices and group moments
  // into a long-form narrative. Helper line is observational; the
  // emotional centre is "the room remembering itself".
  documentary:      'The room remembering itself ✦',
};

export function getBoothCardHelperCopy(productId) {
  return BOOTH_CARD_HELPER_COPY[productId] || null;
}

// Products that are emotionally archival — Section 4 anchors. These
// render with a wider footprint, larger typography, and slower
// hover animation per the iter189 brief. iter200b joins The Final
// Night to this set so it inherits the destination-panel chrome.
export const BOOTH_ARCHIVAL_PRODUCTS = new Set(['full_event_film', 'highlight_reel', 'final_night', 'silent_film', 'documentary']);

// Products that should breathe with a gentle gold glow (iter189 §2).
// Restricted to Lens Notes — emotional centre, restrained signal.
export const BOOTH_BREATHING_PRODUCTS = new Set(['lens_notes']);

// Products that are Archive-only inside Booth OS. On Room they render
// dimmed with the locked copy "Available inside The Archive ✦".
// iter200b — `final_night` is OPTIONAL in The Room (future decision);
// until that decision lands it ships Archive-only inside Booth OS so
// Room hosts see the archival surface in its dimmed "available
// inside The Archive ✦" register rather than as a live participation
// card. Leavers Night uses the dashboard, not Booth OS, so the
// Leavers entry is unaffected by this set.
const ARCHIVE_ONLY_INSIDE_BOOTH_OS = new Set([
  'spin_film_360', 'full_event_film', 'highlight_reel', 'final_night', 'silent_film', 'documentary',
]);

// In-booth capture flows — these don't navigate away. They open a
// view inside Booth OS itself (memory_snap, photo strip, video, flip
// guestbook). The BoothLauncher reads this to decide whether to fire
// onCardPress (in-booth view) or navigate(route) (external surface).
//
// iter195 — Values MUST match BoothOSPage's `onCardPress(id)` switch
// in /app/frontend/src/pages/BoothOSPage.js. Three of these were
// drifting in iter188 ('photo_strip'/'video_message'/'flip_guestbook')
// and silently no-op'd on production for Pocket Booth, Audio/Video
// Guestbook, and Flip Guestbook. Canonical mapping:
//
//    matrix id          →  onCardPress id  →  view
//    ─────────────────────────────────────────────
//    memory_snap        →  'memory_snap'   →  memory_snap
//    pocket_booth       →  'photo'         →  photo_strip
//    video_guestbook    →  'video'         →  video_message
//    memory_guestbook   →  'guestbook'     →  flip_guestbook
export const BOOTH_OS_INLINE_FLOWS = {
  memory_snap: 'memory_snap',
  pocket_booth: 'photo',
  video_guestbook: 'video',
  memory_guestbook: 'guestbook',
};

export const ARCHIVE_LOCKED_COPY = 'Available inside The Archive ✦';

/**
 * Returns the ordered 11-card Booth OS matrix for the given event.
 * Each entry: {product, dimmed, dimmedCopy}.
 *
 *   - Archive : all 11 cards active.
 *   - Room    : 8 active + 3 dimmed (no padlock, no upsell vocab).
 *   - Other   : returns an empty array — non-Booth-OS packages
 *               (Keepsake, experience packs) use getGuestProducts().
 */
export function getBoothOSProducts(event) {
  if (!event) return [];
  const pkg = (event.package_type || '').toLowerCase();
  if (pkg !== 'edition_room' && pkg !== 'edition_archive') return [];
  return BOOTH_OS_CANONICAL_ORDER.map((id) => {
    const product = GUEST_PRODUCT_REGISTRY[id];
    if (!product) return null;
    const dimmed = pkg === 'edition_room' && ARCHIVE_ONLY_INSIDE_BOOTH_OS.has(id);
    return {
      product,
      dimmed,
      dimmedCopy: dimmed ? ARCHIVE_LOCKED_COPY : null,
    };
  }).filter(Boolean);
}

/**
 * iter189 — Sectioned Booth OS view.
 * Returns an array of {section, items[]} preserving the canonical
 * product order *within* each section. Items carry the same shape
 * as getBoothOSProducts() ({product, dimmed, dimmedCopy}) plus an
 * `archival` flag for Section 4 cinematic treatment and a
 * `breathing` flag for the Section 2 emotional centre.
 */
export function getBoothOSSections(event) {
  if (!event) return [];
  const pkg = (event.package_type || '').toLowerCase();
  if (pkg !== 'edition_room' && pkg !== 'edition_archive') return [];
  return BOOTH_OS_SECTIONS.map((section) => ({
    section,
    items: section.products.map((id) => {
      const product = GUEST_PRODUCT_REGISTRY[id];
      if (!product) return null;
      const dimmed = pkg === 'edition_room' && ARCHIVE_ONLY_INSIDE_BOOTH_OS.has(id);
      return {
        product,
        dimmed,
        dimmedCopy: dimmed ? ARCHIVE_LOCKED_COPY : null,
        archival: BOOTH_ARCHIVAL_PRODUCTS.has(id),
        breathing: BOOTH_BREATHING_PRODUCTS.has(id),
        helper: BOOTH_CARD_HELPER_COPY[id] || null,
      };
    }).filter(Boolean),
  })).filter((s) => s.items.length > 0);
}

/**
 * isBoothOSPackage — Room and Archive enter through Booth OS.
 * Keepsake (and any legacy package) stays on the dashboard grid.
 */
export function isBoothOSPackage(event) {
  if (!event) return false;
  const pkg = (event.package_type || '').toLowerCase();
  return pkg === 'edition_room' || pkg === 'edition_archive';
}

// ──────────────────────────────────────────────────────────────────
// iter209 ✦ — Guest Booth OS participation list.
//
// Constitutional brief: a guest standing in a room should see "what
// they can do now", not "what the system will create later". The
// guest Booth OS surface drifted into product-catalogue energy:
// preservation artefacts (Final Night, Silent Film, Documentary,
// highlight reel, full film), live atmosphere surfaces (Memory Wall,
// projector, Remember Us), and operational details all sat next to
// the actual participation actions. Result: cognitive load, loss of
// elegance, the room competing with the interface for attention.
//
// iter213 room-signal (production rehearsal): the iter209 list of
// exactly six actions accidentally removed the most universal
// affordance — uploading a photo/video already on the guest's phone.
// Pocket Booth and Memory Snap are *ceremonial* captures, not
// substitutes for "send the photo you already took". We add Upload
// Memories back as the FIRST card so the lowest-friction path is
// also the most reachable. The ceremonial captures still follow.
//
//   1. Upload Memories       — Already on your phone ✦
//   2. Pocket Booth          — A portrait from tonight ✦
//   3. Memory Snap           — One photograph from each guest ✦
//   4. Audio / Video Gbk     — A voice for tomorrow ✦
//   5. Flip Guestbook        — A page in the keepsake book ✦
//   6. Lens Notes ✦          — Handwriting that stays ✦   ←── emotional centre
//   7. Drop One Word         — A feeling left behind ✦
//
// Lens Notes carries the `centred` gravity flag — the launcher
// renders it with slightly larger type and slightly stronger ink
// contrast so it naturally feels like the deepest contribution
// available, *without* badges, animation, glow, or "featured"
// chrome. Quiet gravity, not loud marketing.
//
// Preservation products (Final Night / Silent Film / Documentary /
// highlight reel / film), live surfaces (Memory Wall / projector /
// atmosphere), and Archive-tier artefacts remain in the system —
// they're surfaced via the operator dashboard, Archive, and What
// Remains, never on the guest entrance.
// ──────────────────────────────────────────────────────────────────
const BOOTH_OS_GUEST_ACTIONS = Object.freeze([
  { id: 'qr_upload',        helper: 'Already on your phone ✦' },
  { id: 'pocket_booth',     helper: 'A portrait from tonight ✦' },
  { id: 'memory_snap',      helper: 'One photograph from each guest ✦' },
  { id: 'video_guestbook',  helper: 'A voice for tomorrow ✦' },
  { id: 'memory_guestbook', helper: 'A page in the keepsake book ✦' },
  { id: 'lens_notes',       helper: 'Handwriting that stays ✦', gravity: 'centred' },
  { id: 'drop_one_word',    helper: 'A feeling left behind ✦' },
]);

/**
 * Returns the 6 guest-Booth-OS actions for the given event in the
 * canonical iter209 order. Each entry is `{ product, helper,
 * gravity }`. Empty array for non-Booth-OS packages — those guests
 * never reach the launcher.
 */
export function getBoothOSGuestActions(event) {
  if (!event) return [];
  const pkg = (event.package_type || '').toLowerCase();
  if (pkg !== 'edition_room' && pkg !== 'edition_archive') return [];
  return BOOTH_OS_GUEST_ACTIONS.map(({ id, helper, gravity }) => {
    const product = GUEST_PRODUCT_REGISTRY[id];
    if (!product) return null;
    return {
      product,
      helper,
      gravity: gravity || 'default',
    };
  }).filter(Boolean);
}

// Public ID list (in order) — exported for tests + any non-renderer
// consumer that needs to assert the canonical participation list
// without instantiating an event object.
export const BOOTH_OS_GUEST_ACTION_IDS = Object.freeze(
  BOOTH_OS_GUEST_ACTIONS.map((a) => a.id),
);

/**
 * iter196 — Leavers Night detection at the guest surface.
 *
 * The guest entrance must take a deliberately different shape for
 * schools: no formal guest list, no invitation name lookup, no
 * "couldn't quietly place that name" softfail. The QR is meant to
 * be passed around the year group — friction must approach zero.
 *
 * This helper is the single source of truth for that branch.
 * EventCompanions.js holds the stewardship-side equivalent; both
 * compare against the same canonical package_type so the surfaces
 * stay in lockstep.
 */
export function isLeaversEdition(event) {
  if (!event) return false;
  return (event.package_type || '').toLowerCase() === 'edition_leavers_night';
}

// ──────────────────────────────────────────────────────────────────
// Legacy resolution helper — Keepsake + experience packs + the
// personal-link GuestDashboard. Used wherever the legacy dashboard
// grid still surfaces.
// ──────────────────────────────────────────────────────────────────
export function getGuestProducts(event) {
  if (!event) return [];
  const pkg = (event.package_type || '').toLowerCase();
  if (PACKAGE_GUEST_PRODUCTS[pkg]) {
    return PACKAGE_GUEST_PRODUCTS[pkg]
      .map((id) => GUEST_PRODUCT_REGISTRY[id])
      .filter(Boolean);
  }
  // Experience-pack / legacy fallback: derive from features[] in
  // Archive order, only include owned products. qr_upload anchored.
  const features = Array.isArray(event.features) ? event.features : [];
  const ordered = PACKAGE_GUEST_PRODUCTS.edition_archive.filter((id) => features.includes(id));
  if (!ordered.includes('qr_upload')) ordered.unshift('qr_upload');
  return ordered.map((id) => GUEST_PRODUCT_REGISTRY[id]).filter(Boolean);
}


// ──────────────────────────────────────────────────────────────────
// iter208 — TAXONOMY LAYERING
// ──────────────────────────────────────────────────────────────────
// The 4-section emotional taxonomy. Used wherever a flat product
// list needs to be re-expressed as the system's actual hierarchy
// instead of an equal-weight grid.
//
// Constitutional rule: products belong to exactly ONE layer. The
// catalogue here is the single source of truth — surfaces import
// `groupProductsByLayer()` rather than re-typing this mapping.
//
// Headings come in two registers, chosen per-surface by the caller:
//
//   • AFTERMATH register (guest-facing surfaces): the participant
//     reads "What remains ✦" — the emotional aftermath, the page
//     resolving into preservation.
//
//   • STEWARDSHIP register (operator-facing surfaces): the host
//     reads "Preserve the night ✦" — the action-state, what the
//     operator is steward of.
//
// Both registers describe the same set of products. The wording
// difference is constitutional, not stylistic — see iter208 brief.
// ──────────────────────────────────────────────────────────────────

// Canonical archive ordering shared across every surface. Final
// Night first (the printed remembrance), Silent Film next (the
// emotional fragment), Documentary the full emotional return, then
// the cinematic cuts that follow them. Per iter208: on Leavers
// (where highlight_reel and full_event_film are hidden), Documentary
// naturally lands last — the deepest emotional landing.
const LAYER_ARCHIVE_ORDER = [
  'final_night',
  'silent_film',
  'documentary',
  'highlight_reel',
  'full_event_film',
];

// Section spine. Each layer declares its product ids in canonical
// order. A product NOT listed below falls through unsectioned —
// callers can decide whether to surface it as a trailing row.
const LAYER_SPINE = [
  {
    id: 'capture',
    titleAftermath:    'Capture the room ✦',
    titleStewardship:  'Capture the room ✦',
    subtitle:          'Moments as they happen.',
    products: ['qr_upload', 'memory_snap', 'pocket_booth', 'spin_film_360'],
  },
  {
    id: 'leave_behind',
    titleAftermath:    'Leave something behind ✦',
    titleStewardship:  'Leave something behind ✦',
    subtitle:          'Words, voices, handwriting, feelings.',
    products: ['lens_notes', 'remember_us', 'drop_one_word', 'video_guestbook', 'memory_guestbook'],
  },
  {
    id: 'see_forming',
    titleAftermath:    'See the room forming ✦',
    titleStewardship:  'See the room forming ✦',
    subtitle:          'The atmosphere, live.',
    products: ['memory_wall_card'],
  },
  {
    id: 'preserve',
    // The constitutional language-polarity decision from iter208:
    //   guest-facing  → aftermath  → "What remains ✦"
    //   operator      → stewardship → "Preserve the night ✦"
    titleAftermath:    'What remains ✦',
    titleStewardship:  'Preserve the night ✦',
    subtitle:          'What the room becomes afterwards.',
    products: LAYER_ARCHIVE_ORDER,
  },
];

/**
 * Group a flat product list into the 4 emotional layers.
 *
 * @param {Array<{id: string}>} products — output of getGuestProducts()
 *   (or any flat product list whose entries carry an id).
 * @param {Object} options
 * @param {'aftermath'|'stewardship'} options.register — heading
 *   register: 'aftermath' for guest-facing surfaces, 'stewardship'
 *   for operator-facing surfaces. Defaults to 'aftermath' because
 *   guest surfaces are the most common caller.
 * @returns {Array<{id, title, subtitle, items}>}
 *
 * Layers with zero items are dropped — the surface never renders
 * an empty section. Order is constitutional (capture → leave_behind
 * → see_forming → preserve); the caller does not get to override it.
 */
export function groupProductsByLayer(products, { register = 'aftermath' } = {}) {
  if (!Array.isArray(products) || products.length === 0) return [];
  const byId = new Map(products.map((p) => [p.id, p]));
  const out = [];
  for (const layer of LAYER_SPINE) {
    const items = [];
    for (const pid of layer.products) {
      const p = byId.get(pid);
      if (p) items.push(p);
    }
    if (items.length === 0) continue;
    out.push({
      id: layer.id,
      title: register === 'stewardship' ? layer.titleStewardship : layer.titleAftermath,
      subtitle: layer.subtitle,
      items,
    });
  }
  return out;
}

// Exported so EventCompanions (a separate catalogue) can share the
// same layer spine without re-typing it. Companions opt in by id.
export const LAYER_SPINE_PUBLIC = LAYER_SPINE.map((l) => ({
  id: l.id,
  titleAftermath: l.titleAftermath,
  titleStewardship: l.titleStewardship,
  subtitle: l.subtitle,
  productIds: [...l.products],
}));
